import { Chart as ChartJS } from "chart.js";
import { jsPDF } from "jspdf";
import { COLOR_SCALE } from "../config/colorScale";

export interface ExportStats {
  keywords: number;
  totalVolume: number;
  avgDifficulty: number;
  lastRefreshed: string;
  nextUpdate: string;
}

export interface ExportLegendItem {
  color: string;
  label: string;
  border?: string;
  // "diamond" renders a rotated square (the cluster-center marker); defaults to
  // a circle, matching the round keyword bubbles.
  shape?: "circle" | "diamond";
}

export interface ExportOptions {
  chartInstance: ChartJS;
  includeLabels: boolean;
  format: "jpeg" | "pdf";
  domainName: string;
  anchorKeyword: string;
  logoPath?: string;
  stats: ExportStats;
  filename: string;
  // Whether the chart draws the cluster-center diamond, so the exported legend
  // lists it only when it's actually plotted (mirrors ColorLegends).
  showCenterMarker?: boolean;
}

const EXPORT_PIXEL_RATIO = 4;
const PURPLE = "#7C3AED";
const LAVENDER_BG = "#F3F0FF";
const ANCHOR_RED = "#EE693F";
// Neutral swatch for the cluster-center diamond; its real color varies with
// trend, so the legend key stays neutral (matches the on-screen ColorLegends).
const CENTER_NEUTRAL = "#BDB9C1";

const loadImage = (src: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

const captureChart = async (
  chart: ChartJS,
  includeLabels: boolean,
): Promise<HTMLImageElement> => {
  const datalabelsOptions = (chart.options.plugins as any)?.datalabels;
  const originalDisplay = datalabelsOptions?.display;
  const originalDpr = chart.options.devicePixelRatio;

  try {
    // Global toggle only. The center diamond stays unlabeled via its
    // dataset-level datalabels override, so no scriptable display is needed
    // here (a scriptable display triggers a datalabels recursion on capture).
    if (datalabelsOptions) datalabelsOptions.display = includeLabels;
    chart.options.devicePixelRatio = EXPORT_PIXEL_RATIO;
    chart.resize();
    chart.draw();
    return await loadImage(chart.toBase64Image("image/png", 1));
  } finally {
    if (datalabelsOptions) datalabelsOptions.display = originalDisplay;
    chart.options.devicePixelRatio = originalDpr;
    chart.resize();
    chart.draw();
  }
};

const drawRoundRect = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
) => {
  ctx.beginPath();
  ctx.roundRect(x, y, w, h, r);
};

const drawHeader = (
  ctx: CanvasRenderingContext2D,
  canvasW: number,
  headerH: number,
  scale: number,
  domainName: string,
  anchorKeyword: string,
  logoImg: HTMLImageElement | null,
) => {
  ctx.fillStyle = LAVENDER_BG;
  ctx.fillRect(0, 0, canvasW, headerH);

  const padX = 24 * scale;
  const padY = 20 * scale;

  // Domain name
  ctx.fillStyle = PURPLE;
  ctx.font = `600 ${11 * scale}px Inter, Arial, sans-serif`;
  ctx.fillText(domainName.toUpperCase(), padX, padY + 11 * scale);

  // Anchor keyword
  ctx.fillStyle = "#1a202c";
  ctx.font = `700 ${18 * scale}px Inter, Arial, sans-serif`;
  ctx.fillText(anchorKeyword, padX, padY + 11 * scale + 24 * scale);

  // Logo top-right
  if (logoImg) {
    const logoW = 120 * scale;
    const logoH = logoW * (logoImg.height / logoImg.width);
    const logoX = canvasW - padX - logoW;
    const logoY = (headerH - logoH) / 2;
    ctx.drawImage(logoImg, logoX, logoY, logoW, logoH);
  }
};

const drawStatCards = (
  ctx: CanvasRenderingContext2D,
  canvasW: number,
  y: number,
  sectionH: number,
  scale: number,
  stats: ExportStats,
) => {
  ctx.fillStyle = LAVENDER_BG;
  ctx.fillRect(0, y, canvasW, sectionH);

  const cards = [
    { label: "KEYWORDS", value: String(stats.keywords) },
    { label: "TOTAL VOLUME", value: stats.totalVolume.toLocaleString() },
    { label: "AVG. DIFFICULTY", value: String(stats.avgDifficulty) },
    { label: "LAST REFRESHED", value: stats.lastRefreshed, bold: true },
    { label: "NEXT UPDATE", value: stats.nextUpdate, bold: true },
  ];

  const padX = 24 * scale;
  const padY = 12 * scale;
  const gap = 10 * scale;
  const totalGap = padX * 2 + gap * (cards.length - 1);
  const cardW = (canvasW - totalGap) / cards.length;
  const cardH = sectionH - padY * 2;
  const radius = 8 * scale;

  cards.forEach((card, i) => {
    const cx = padX + i * (cardW + gap);
    const cy = y + padY;

    // White card
    ctx.save();
    ctx.shadowColor = "rgba(0,0,0,0.06)";
    ctx.shadowBlur = 4 * scale;
    ctx.fillStyle = "#ffffff";
    drawRoundRect(ctx, cx, cy, cardW, cardH, radius);
    ctx.fill();
    ctx.restore();

    const innerPadX = 12 * scale;
    const innerPadY = 10 * scale;

    // Label
    ctx.fillStyle = "#6b7280";
    ctx.font = `500 ${9 * scale}px Inter, Arial, sans-serif`;
    ctx.fillText(card.label, cx + innerPadX, cy + innerPadY + 9 * scale);

    // Value
    ctx.fillStyle = "#1a202c";
    const valueFontSize = card.bold ? 13 * scale : 18 * scale;
    ctx.font = `${card.bold ? 600 : 700} ${valueFontSize}px Inter, Arial, sans-serif`;
    ctx.fillText(card.value, cx + innerPadX, cy + cardH - innerPadY);
  });
};

const LEGEND_ITEMS: ExportLegendItem[] = [
  { color: COLOR_SCALE[0], label: "Strong Negative Trend" },
  { color: COLOR_SCALE[1], label: "Weak Negative Trend" },
  { color: COLOR_SCALE[2], label: "Neutral / No Trend" },
  { color: COLOR_SCALE[3], label: "Weak Positive Trend" },
  { color: COLOR_SCALE[4], label: "Strong Positive Trend" },
  { color: ANCHOR_RED, label: "Anchor Keyword", border: "#c0392b" },
  { color: CENTER_NEUTRAL, label: "Cluster Weighted Average", shape: "diamond" },
];

const drawLegend = (
  ctx: CanvasRenderingContext2D,
  canvasW: number,
  y: number,
  legendH: number,
  scale: number,
  showCenterMarker: boolean,
) => {
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, y, canvasW, legendH);

  // Drop the diamond entry when the chart doesn't plot a center.
  const items = showCenterMarker
    ? LEGEND_ITEMS
    : LEGEND_ITEMS.filter((item) => item.shape !== "diamond");

  const dotR = 7 * scale;
  const padX = 24 * scale;
  const itemGap = (canvasW - padX * 2) / items.length;
  const cy = y + legendH / 2;

  items.forEach((item, i) => {
    const ix = padX + i * itemGap;

    if (item.shape === "diamond") {
      // Rotated square, sized so its diagonal matches the circle diameter.
      const half = dotR / Math.SQRT2;
      ctx.save();
      ctx.translate(ix + dotR, cy);
      ctx.rotate(Math.PI / 4);
      ctx.fillStyle = item.color;
      ctx.fillRect(-half, -half, half * 2, half * 2);
      ctx.restore();
    } else {
      ctx.beginPath();
      ctx.arc(ix + dotR, cy, dotR, 0, Math.PI * 2);
      ctx.fillStyle = item.color;
      ctx.fill();
      if (item.border) {
        ctx.strokeStyle = item.border;
        ctx.lineWidth = 1.5 * scale;
        ctx.stroke();
      }
    }

    ctx.fillStyle = "#374151";
    ctx.font = `400 ${10 * scale}px Inter, Arial, sans-serif`;
    ctx.fillText(item.label, ix + dotR * 2 + 6 * scale, cy + 4 * scale);
  });
};

const drawFooter = (
  ctx: CanvasRenderingContext2D,
  canvasW: number,
  y: number,
  footerH: number,
  scale: number,
) => {
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, y, canvasW, footerH);

  ctx.fillStyle = "#9ca3af";
  ctx.font = `400 ${10 * scale}px Inter, Arial, sans-serif`;
  ctx.textAlign = "center";
  ctx.fillText(
    "Volume vs Difficulty  ·  Size = Ranking  ·  Color = Trend",
    canvasW / 2,
    y + footerH / 2 + 4 * scale,
  );
  ctx.textAlign = "left";
};

const triggerDownload = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

export const exportChart = async (options: ExportOptions): Promise<void> => {
  const {
    chartInstance,
    includeLabels,
    format,
    domainName,
    anchorKeyword,
    logoPath = "/clusterview-logo-with-text.svg",
    stats,
    filename,
    showCenterMarker = false,
  } = options;

  const chartImg = await captureChart(chartInstance, includeLabels);

  let logoImg: HTMLImageElement | null = null;
  try {
    logoImg = await loadImage(logoPath);
  } catch {
    console.warn("Could not load logo, proceeding without it.");
  }

  const scale = EXPORT_PIXEL_RATIO;
  const chartW = chartImg.width;
  const chartH = chartImg.height;

  const headerH = 80 * scale;
  const statsH = 90 * scale;
  const legendH = 40 * scale;
  const footerH = 32 * scale;
  const totalH = headerH + statsH + chartH + legendH + footerH;

  const canvas = document.createElement("canvas");
  canvas.width = chartW;
  canvas.height = totalH;
  const ctx = canvas.getContext("2d")!;
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";

  // Background
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  drawHeader(ctx, chartW, headerH, scale, domainName, anchorKeyword, logoImg);
  drawStatCards(ctx, chartW, headerH, statsH, scale, stats);

  // Chart
  ctx.drawImage(chartImg, 0, headerH + statsH, chartW, chartH);

  drawLegend(ctx, chartW, headerH + statsH + chartH, legendH, scale, showCenterMarker);
  drawFooter(ctx, chartW, headerH + statsH + chartH + legendH, footerH, scale);

  if (format === "jpeg") {
    canvas.toBlob(
      (blob) => {
        if (blob) triggerDownload(blob, filename);
      },
      "image/jpeg",
      0.95,
    );
  } else {
    // PDF — page sized exactly to the canvas dimensions (96 DPI → mm conversion)
    const PX_TO_MM = 25.4 / 96;
    const pdfW = canvas.width * PX_TO_MM;
    const pdfH = canvas.height * PX_TO_MM;
    const pdf = new jsPDF({
      orientation: pdfW > pdfH ? "landscape" : "portrait",
      unit: "mm",
      format: [pdfW, pdfH],
    });
    const dataUrl = canvas.toDataURL("image/jpeg", 0.95);
    pdf.addImage(dataUrl, "JPEG", 0, 0, pdfW, pdfH);
    pdf.save(filename);
  }
};
